import { useMutation, useQuery } from "@tanstack/react-query"
import {
  IPatientSurvey,
  ISurvey,
  PatientsSurveysGetResponse,
  PatientsSurveysSubmissionPostBody,
  SurveysGetResponse
} from "../types/types"
import { apiFetchProtected } from "../utils/api"

const survey_references_order = [
  "filter_phase_2_consultation_infos",
  "filter_consultation_infos",
  "filter_personal_informations",
  "filter_medical_information",
  "life_quality",
  "filter_pain_walk_scale"
]

const lumbar_survey_references_phase_2_order = [
  "filter_phase_2_consultation_infos",
  "filter_personal_informations",
  "filter_phase_2_medical_information",
  "life_quality",
  "filter_pain_walk_scale"
]

const cervical_survey_references_phase_2_order = [
  "filter_phase_2_consultation_infos",
  "filter_personal_informations",
  "filter_cervical_medical_information",
  "neck_disability_index",
  "filter_cervical_pain_neck_scale"
]

const questionsOrdered: Record<string, Array<string>> = {
  filter_phase_2_consultation_infos: [
    "filter_consultation_date_existence",
    "filter_consultation_date",
    "phone_number"
  ],
  filter_personal_informations: [
    "gender",
    "weight",
    "height",
    "professional_category",
    "average_physical_activity"
  ],
  filter_medical_information: [
    "back_surgery_history",
    "principal_consultation_reason",
    "principal_symptom_side",
    "work_accident",
    "filter_muscular_weakness",
    "filter_muscular_weakness_comment",
    "urinal_problems",
    "symptoms_beginning",
    "lumbar_imaging",
    "lumbar_infiltration"
  ],
  filter_phase_2_medical_information: [
    "back_surgery_history",
    "principal_consultation_reason",
    "lumbar_pain_fall_link",
    "lumbar_pain_fracture_link",
    "principal_symptom_side",
    "work_accident",
    "filter_muscular_weakness",
    "filter_muscular_weakness_comment",
    "urinal_problems",
    "urinal_problems_comment",
    "symptoms_beginning",
    "lumbar_imaging",
    "lumbar_infiltration",
    "physiotherapy"
  ],
  filter_cervical_medical_information: [
    "neck_surgery_history",
    "principal_cervical_consultation_reason",
    "principal_symptom_side",
    "work_accident",
    "filter_upper_body_muscular_weakness",
    "filter_upper_body_muscular_weakness_comment",
    "symptoms_beginning",
    "cervical_imaging",
    "cervical_infiltration",
    "cervical_physiotherapy"
  ],
  neck_disability_index: [
    "cervical_pain_level",
    "personal_hygiene_autonomy",
    "load_handling",
    "reading_capacity",
    "headache_prevalence",
    "focus_span",
    "work_capacity",
    "driving_capacity",
    "sleep_quality",
    "leasure_activities_capacity"
  ]
}

const lumbarSurveys = [
  "filter_personal_informations",
  "filter_phase_2_medical_information",
  "filter_phase_2_consultation_infos",
  "filter_medical_information"
]

const cervicalSurveys = [
  "filter_personal_informations",
  "filter_phase_2_consultation_infos",
  "filter_cervical_medical_information",
  "neck_disability_index"
]

const orderQuestions = (survey: ISurvey | undefined) => {
  if (
    survey &&
    (lumbarSurveys.includes(survey.survey_reference) ||
      cervicalSurveys.includes(survey.survey_reference))
  ) {
    const questions = questionsOrdered[survey.survey_reference].map(
      (_q) =>
        survey.questions.find(
          (question) => question.question_reference === _q
        ) || survey.questions[0]
    )
    return { ...survey, questions }
  }
  return survey
}

const sortByList = (list: string[], arr: IPatientSurvey[]) => {
  const tmpMap = arr.reduce(
    (acc: { [key: string]: IPatientSurvey }, item: IPatientSurvey) => {
      acc[item.survey_reference] = item
      return acc
    },
    {}
  )

  return list.map((key) => tmpMap[key])
}

const getList2SortBy = (
  patientFunnel: string,
  patientSurgeryCategory: string,
  recommended: boolean
) => {
  let list2SortBy = recommended ? ["appointement_recommandation"] : []
  if (patientFunnel === "filter_phase_2") {
    return list2SortBy.concat(
      patientSurgeryCategory === "lumbar"
        ? lumbar_survey_references_phase_2_order
        : cervical_survey_references_phase_2_order
    )
  } else return list2SortBy.concat(survey_references_order)
}

const fetchPatientSurveys = async (
  patientId: string
): Promise<PatientsSurveysGetResponse> =>
  await apiFetchProtected<PatientsSurveysGetResponse>(
    `/patients/${patientId}/patient_surveys`,
    {}
  )

export const fetchSurveys = async (): Promise<SurveysGetResponse> =>
  await apiFetchProtected<SurveysGetResponse>("/surveys", {})

export const usePostPatientSurveySubmission = (
  patient_id: number,
  patient_survey_id: number
) => {
  return useMutation({
    mutationFn: (patientSurveySubmission: PatientsSurveysSubmissionPostBody) =>
      apiFetchProtected(
        `/patients/${patient_id}/patient_surveys/${patient_survey_id}/submission`,
        {
          method: "POST",
          body: patientSurveySubmission
        }
      ),
    onError: (error) => {
      console.error("Error:", error)
    }
  })
}

export const useSurveys = <TData = SurveysGetResponse>(
  select?: (data: SurveysGetResponse) => TData
) =>
  useQuery({
    queryKey: ["surveys"],
    queryFn: () => fetchSurveys(),
    select
  })

export const usePatientSurveys = <TData = PatientsSurveysGetResponse>(
  patientId: string,
  select?: (data: PatientsSurveysGetResponse) => TData
) =>
  useQuery({
    queryKey: ["patient_surveys"],
    queryFn: () => fetchPatientSurveys(patientId),
    select
  })

export const useActiveAndNotCompletedPatientsSurveys = (
  patientId: string,
  patientFunnel = "sumo",
  patientSurgeryCategory = "lumbar",
  recommended = false
) =>
  usePatientSurveys(patientId, (data: PatientsSurveysGetResponse) => {
    const filteredPatientSurveys = data.filter(
      (patientSurvey: IPatientSurvey) => {
        return (
          patientSurvey.active &&
          patientSurvey.completed === false &&
          patientSurvey.survey_reference !== "filter_prediction_feedback"
        )
      }
    )
    const list2SortBy = getList2SortBy(
      patientFunnel,
      patientSurgeryCategory,
      recommended
    )
    const sortedPatientSurveys = sortByList(list2SortBy, filteredPatientSurveys)
    return sortedPatientSurveys.filter(
      (patientSurvey) => patientSurvey !== undefined
    )
  })

export const useCompletedPatientsSurveys = (
  patientId: string,
  patientFunnel = "sumo",
  patientSurgeryCategory = "lumbar",
  recommended = false
) =>
  usePatientSurveys(patientId, (data: PatientsSurveysGetResponse) => {
    const filteredPatientSurveys = data.filter(
      (patientSurvey: IPatientSurvey) => {
        return (
          patientSurvey.completed === true &&
          ![
            "filter_prediction_feedback",
            "filter_phase_2_consultation_date_period",
            "filter_phase_2_consultation_date_choice",
            "filter_phase_2_prediction_feedback"
          ].includes(patientSurvey.survey_reference)
        )
      }
    )
    const sortedPatientSurveys = sortByList(
      getList2SortBy(patientFunnel, patientSurgeryCategory, recommended),
      filteredPatientSurveys
    )
    return sortedPatientSurveys.filter(
      (patientSurvey) => patientSurvey !== undefined
    )
  })

export const useSurveysFromNames = (
  surveyNamesList: Array<string> | undefined
) =>
  useSurveys((data: SurveysGetResponse) =>
    data.filter((survey) => surveyNamesList?.includes(survey.survey_reference))
  )

export const useSurveysFromName = (surveyName: string) =>
  useSurveys((data: SurveysGetResponse) => {
    const survey = data.find((survey) => surveyName === survey.survey_reference)
    return orderQuestions(survey)
  })
